import Formatter from '../Formatter';

class Chart {
  static series = [];
  static colorPalette = [
    '#1ABC9C',
    '#0089C7',
    '#DE6C47',
    '#FFB311',
    '#F9F966',
    '#d13814',
    '#9b59b6',
    '#f17cb0',
    '#84754e',
    '#003666',
    '#EACA69',
    '#00B4FF',
    '#F97566',
  ];
  static colorPalette1 = [
    '#508A49',
    '#6BA752',
    '#33653B',
    '#8DB452',
    '#1ABC9C',
    '#0089C7',
  ];

  constructor(
    chartObject,
    height = 'auto',
    showBackground = false,
    percent = false,
    showZeros = false
  ) {
    this.chartObject = chartObject;
    this.percent = percent;
    this.series = chartObject.values;
    this.height = height;
    this.showBackground = showBackground;
    this.showZeros = showZeros;
  }

  /**
   * Obtener la paleta de color
   * @returns array
   */
  static getColorPalette() {
    return this.colorPalette;
  }

  /**
   * Obtener la paleta de color1
   * @returns array
   */
  static getColorPalette1() {
    return this.colorPalette1;
  }

  /**
   * Verifica si se muestran los labels de la tabla
   * @param {String} index
   */
  showDataLabels(index) {
    let showDataLabels = true;
    try {
      showDataLabels = this.chartObject.plotOptions.showDataLabels;
    } catch (error) {
      if (
        this.chartObject.plotOptions &&
        this.chartObject.plotOptions[index] &&
        this.chartObject.plotOptions[index].dataLabels
      )
        showDataLabels = this.chartObject.plotOptions[index].dataLabels.enabled;
    }

    return showDataLabels;
  }

  /**
   * Verifica si al pasar el mouse sobre la data de la gráfica
   * se muestra la información del valor
   * @param {String} index
   */
  isEnableMouseTracking(index) {
    let enableMouseTracking = true;
    try {
      enableMouseTracking = this.chartObject.plotOptions.enableMouseTracking;
    } catch (error) {
      if (this.chartObject.plotOptions && this.chartObject.plotOptions[index])
        enableMouseTracking =
          this.chartObject.plotOptions[index].enableMouseTracking;
    }

    return enableMouseTracking;
  }

  /**
   * Obtener el valor del sufijo para la gráfica
   */
  showValueSuffixFromOrigin() {
    try {
      return this.chartObject.values.some((value) => value.tooltip);
    } catch (error) {
      return false;
    }
  }

  /**
   * Obtener el valor para el tooltip
   * @param dataChart
   * @param labelName
   */
  getTooltip(dataChart, labelName = null) {
    const value = Formatter.formatMoney(dataChart.y, this.chartObject.decimal);
    let suffix = this.getSuffix(dataChart.series);

    const tooltip = `<span style="font-size: 10px">${dataChart.key}</span>
            <br/>
            <span style="color:${dataChart.series.color}">\u25CF</span>
            ${labelName ? labelName : dataChart.series.name}: <strong>${value}${
      this.percent ? ' %' : suffix
    }</strong><br/>`;

    return tooltip;
  }

  /**
   * Obtener el tamaño de la data de las series de la gráfica
   * Permite 0 y 1 | 0 = tamaño de la posición inicial, 1 = tamaño de la serie total
   * y permite 2 = tamaño de ambos como un arreglo, donde la posición 0 será el tamaño
   * de la posición inicial y la posición 1 será el tamaño de la serie total
   * @param option
   */
  getDataSeriesLength(option) {
    try {
      if (option === 0) return this.chartObject.values[0].data.length;
      if (option === 1) return this.chartObject.values.length;
      if (option === 2)
        return [
          this.chartObject.values[0].data.length,
          this.chartObject.values.length,
        ];
    } catch (error) {
      return 0;
    }

    return 0;
  }

  /**
   * Obtiene el valor de las decimas a mostrar en la gráfica
   */
  getDataNumbersDecimal() {
    try {
      if (this.chartObject.decimal) return this.chartObject.decimal;
    } catch (error) {
      return 2;
    }

    return 2;
  }

  /**
   * Obtener el tipo de columna para la gráfica
   * @param {string} defaultType
   * @returns {string}
   */
  getChartType(defaultType) {
    return this.chartObject.type ? this.chartObject.type : defaultType;
  }

  /**
   * Obtener el sufijo de la data
   * @param series
   * @returns {string}
   */
  getSuffix(series) {
    const suffix = series.tooltipOptions.valueSuffix;

    return suffix ? suffix : '';
  }

  /**
   * Obtener el grado de ratación de los label del eje X
   * @returns {number}
   */
  getRotationLabel() {
    let rotationLabel = this.chartObject.rotationLabel;
    const xAxisRotationLabel = this.chartObject.xAxis.labels;

    rotationLabel = rotationLabel
      ? rotationLabel
      : xAxisRotationLabel
      ? xAxisRotationLabel.rotation
      : null;

    return rotationLabel ? rotationLabel : null;
  }

  /**
   * Obtener el grado de ratación de los label del eje X
   * @returns {number}
   */
  getXAxisName() {
    const xAxisName = this.chartObject.xAxisName;

    return xAxisName ? { text: xAxisName } : {};
  }

  /**
   * Obtener las bandas de trama para la gráfica
   * @returns {Object}
   */
  getPlotBands() {
    if (!this.showBackground) return {};

    const plotBands = this.chartObject.plotBands;

    return plotBands ? plotBands : {};
  }

  /**
   * Obtener la línea de revisión para la gráfica
   * @returns {Object}
   */
  getPlotLines() {
    const plotLines = this.chartObject.plotLines;

    return plotLines ? { plotLines: plotLines } : {};
  }

  /**
   * Obtener los datos de ticks que se dibujarán en el eje
   * @returns {Object}
   */
  getTicksData() {
    const tickAmount = this.chartObject.yAxis.tickAmount;
    const endOnTick = this.chartObject.yAxis.endOnTick;

    if (!tickAmount) return null;

    return { tickAmount, endOnTick };
  }

  /**
   * Convertir la data para que funcione para gráfica de torta
   * @returns
   */
  toPieConvert() {
    const decimal = this.getDataNumbersDecimal();
    const obj = Array.from(
      { length: this.getDataSeriesLength(1) },
      (_, index) => index
    ).map((i) => {
      return {
        name: this.chartObject.values[i].name,
        y: this.chartObject.values[i].data[0],
        color: this.chartObject.values[i].color,
        sliced: i === 0,
        selected: i == 0,
      };
    });

    return {
      STitulo: this.chartObject.STitulo,
      Titulo: this.chartObject.Titulo,
      decimal: decimal,
      values: [
        {
          name: this.chartObject.Titulo,
          colorByPoint: true,
          data: obj,
        },
      ],
    };
  }
}

export default Chart;
